// Collection Toggle Component
// Simple, direct implementation without custom elements

function CollectionToggle(element) {
  this.toggle = element;
  this.options = element.querySelectorAll('.collection-toggle__option');
  this.slider = element.querySelector('.collection-toggle__slider');
  this.section = element.closest('.featured-collection-toggle-section') || element.closest('[data-section-type="featured-collection-toggle"]');
  this.panels = this.section ? this.section.querySelectorAll('[data-collection-panel]') : [];
  this.contentContainer = this.section ? this.section.querySelector('[data-toggle-content]') : null;
  this.currentIndex = 0;
  this.isAnimating = false;
  
  this.init();
}

CollectionToggle.prototype.init = function() {
  if (!this.options.length || !this.slider) {
    console.warn('Collection toggle: Required elements not found');
    return;
  }

  console.log('Collection toggle found:', this.options.length, 'options and', this.panels.length, 'panels');

  // Set up accessibility
  this.setupAccessibility();
  
  // Bind events
  this.bindEvents();
  
  // Check URL parameters for initial state
  this.handleUrlParameters();
  
  // Initial state
  this.updateSlider();
  this.updateOptions();
  this.updatePanels();
  this.updateActiveState();
  
  console.log('Collection toggle initialized');
};

CollectionToggle.prototype.setupAccessibility = function() {
  var self = this;
  this.toggle.setAttribute('role', 'tablist');
  
  this.options.forEach(function(option, index) {
    option.setAttribute('role', 'tab');
    option.setAttribute('aria-selected', index === self.currentIndex ? 'true' : 'false');
    option.setAttribute('tabindex', index === self.currentIndex ? '0' : '-1');
    option.setAttribute('id', 'toggle-tab-' + index);
  });

  this.panels.forEach(function(panel, index) {
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', 'toggle-tab-' + index);
    panel.setAttribute('tabindex', '0');
  });
};

CollectionToggle.prototype.bindEvents = function() {
  var self = this;
  this.options.forEach(function(option, index) {
    option.addEventListener('click', function(e) {
      self.handleOptionClick(e, index);
    });
    option.addEventListener('keydown', function(e) {
      self.handleKeydown(e, index);
    });
  });
};

CollectionToggle.prototype.handleOptionClick = function(event, index) {
  event.preventDefault();
  if (this.isAnimating || index === this.currentIndex) return;
  
  // Remove focus on mobile to prevent stuck hover states
  if ('ontouchstart' in window) {
    event.target.blur();
  }
  
  this.switchToIndex(index);
};

CollectionToggle.prototype.handleKeydown = function(event, index) {
  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault();
      this.switchToIndex(Math.max(0, index - 1));
      break;
    case 'ArrowRight':
      event.preventDefault();
      this.switchToIndex(Math.min(this.options.length - 1, index + 1));
      break;
    case 'Home':
      event.preventDefault();
      this.switchToIndex(0);
      break;
    case 'End':
      event.preventDefault();
      this.switchToIndex(this.options.length - 1);
      break;
  }
};

CollectionToggle.prototype.switchToIndex = function(newIndex, updateUrl) {
  if (this.isAnimating || newIndex === this.currentIndex) return;
  
  var self = this;
  this.isAnimating = true;
  
  // Update current index
  var previousIndex = this.currentIndex;
  this.currentIndex = newIndex;
  
  // Update visual states
  this.updateSlider();
  this.updateOptions();
  this.updateAccessibility();
  
  // Update content with transition
  this.updatePanels().then(function() {
    self.isAnimating = false;
    self.updateActiveState();
    
    // Update URL if requested (default true for user clicks)
    if (updateUrl !== false) {
      self.updateUrl();
    }
    
    // Dispatch custom event
    var event = new CustomEvent('collection-toggle:change', {
      detail: { 
        previousIndex: previousIndex, 
        currentIndex: self.currentIndex,
        handle: self.options[self.currentIndex] ? self.options[self.currentIndex].dataset.collectionHandle : null
      }
    });
    
    if (self.toggle) {
      self.toggle.dispatchEvent(event);
    }
  });
};

CollectionToggle.prototype.updateSlider = function() {
  if (!this.slider) return;
  
  var translateX = this.currentIndex * 100;
  this.slider.style.transform = 'translateX(' + translateX + '%)';
};

CollectionToggle.prototype.updateOptions = function() {
  var self = this;
  this.options.forEach(function(option, index) {
    var isActive = index === self.currentIndex;
    if (isActive) {
      option.classList.add('collection-toggle__option--active');
    } else {
      option.classList.remove('collection-toggle__option--active');
    }
    option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
};

CollectionToggle.prototype.updateAccessibility = function() {
  var self = this;
  this.options.forEach(function(option, index) {
    var isActive = index === self.currentIndex;
    option.setAttribute('aria-selected', isActive ? 'true' : 'false');
    option.setAttribute('tabindex', isActive ? '0' : '-1');
  });
  
  // Focus management
  if (document.activeElement && Array.prototype.indexOf.call(this.options, document.activeElement) !== -1) {
    this.options[this.currentIndex].focus();
  }
};

CollectionToggle.prototype.updatePanels = function() {
  if (!this.panels.length || !this.contentContainer) return Promise.resolve();
  
  var self = this;
  
  // Fade out current panel
  this.contentContainer.style.opacity = '0.6';
  
  return new Promise(function(resolve) {
    setTimeout(function() {
      // Show/hide panels using CSS classes
      self.panels.forEach(function(panel, index) {
        var isActive = index === self.currentIndex;
        if (isActive) {
          panel.classList.remove('collection-toggle-panel--hidden');
        } else {
          panel.classList.add('collection-toggle-panel--hidden');
        }
        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
      
      // Fade in new panel
      self.contentContainer.style.opacity = '1';
      
      setTimeout(resolve, 150);
    }, 150);
  });
};

CollectionToggle.prototype.updateActiveState = function() {
  if (this.toggle) {
    this.toggle.setAttribute('data-active', this.currentIndex.toString());
  }
};

CollectionToggle.prototype.handleUrlParameters = function() {
  var urlParams = new URLSearchParams(window.location.search);
  var typeParam = urlParams.get('type');
  
  if (typeParam) {
    // Find matching option by text content
    for (var i = 0; i < this.options.length; i++) {
      var optionText = this.options[i].textContent.trim();
      if (optionText.toLowerCase() === typeParam.toLowerCase()) {
        this.currentIndex = i;
        console.log('URL parameter matched:', typeParam, 'to index', i);
        break;
      }
    }
  }
};

CollectionToggle.prototype.updateUrl = function() {
  if (!this.options[this.currentIndex]) return;
  
  var activeOptionText = this.options[this.currentIndex].textContent.trim();
  var url = new URL(window.location);
  
  // Update the type parameter
  url.searchParams.set('type', activeOptionText);
  
  // Update URL without page reload
  window.history.replaceState({}, '', url.toString());
};

// Initialize toggles
function initializeToggle(toggleElement) {
  var toggle = new CollectionToggle(toggleElement);
  toggleElement._toggleInstance = toggle;
  return toggle;
}

// Initialize toggles when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('[data-collection-toggle]').forEach(function(toggle) {
    if (!toggle._toggleInstance) {
      initializeToggle(toggle);
    }
  });
});

// Also initialize immediately for any existing toggles (in case DOM is already loaded)
if (document.readyState === 'loading') {
  // DOM is still loading, DOMContentLoaded will handle it
} else {
  // DOM is already loaded
  document.querySelectorAll('[data-collection-toggle]').forEach(function(toggle) {
    if (!toggle._toggleInstance) {
      initializeToggle(toggle);
    }
  });
}

// Handle theme section updates in Shopify admin
document.addEventListener('shopify:section:load', function(event) {
  var toggles = event.target.querySelectorAll('[data-collection-toggle]');
  
  toggles.forEach(function(toggle) {
    if (!toggle._toggleInstance) {
      initializeToggle(toggle);
    }
  });
});

// Handle browser back/forward buttons
window.addEventListener('popstate', function() {
  document.querySelectorAll('[data-collection-toggle]').forEach(function(toggle) {
    if (toggle._toggleInstance) {
      toggle._toggleInstance.handleUrlParameters();
      toggle._toggleInstance.updateSlider();
      toggle._toggleInstance.updateOptions();
      toggle._toggleInstance.updatePanels();
      toggle._toggleInstance.updateActiveState();
    }
  });
});

// Export for external use if needed
window.CollectionToggle = CollectionToggle;
window.initializeToggle = initializeToggle;